import { useState, useEffect, useCallback, useMemo } from "react"
import { Alert, message } from "antd"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { swimminglyApi, gen, getActingUserRequestFields } from "./utils"
import { updateSeasonMeetArrayListener } from "./MeetSchedulePageNew"
import { IClubSeasonInformation } from "./MeetActionsModal"
import PoolSelectionModal, { IPoolSelectionOption } from "./PoolSelectionModal"

interface IRegularMeetCourseModalProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  meetId: number
  clubSeason: IClubSeasonInformation | null
  meetClubSeasons: IClubSeasonInformation[]
  refresh?: () => void
}

interface IPool {
  poolId: number | null
  clubId?: number | null
  clubName?: string | null
  pool_name: string | null
  course_label: "SCY" | "SCM" | "LCM" | null
  pool_length: number | null
  address: string | null
  description?: string | null
  lat?: number | null
  lng?: number | null
  unit_of_measure?: number | null
  numLanes?: number | null
  primaryPool?: boolean
}

export default function RegularMeetCourseModal({
  visible,
  setVisible,
  meetId,
  clubSeason,
  meetClubSeasons,
  refresh,
}: IRegularMeetCourseModalProps): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const actingUser = aliasedUser || user
  const [currentPool, setCurrentPool] = useState<IPool | null>(null)
  const [newPool, setNewPool] = useState<IPool | null>(null)
  const [allPools, setAllPools] = useState<IPool[] | null>(null)
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null)
  const [mounted, setMounted] = useState(false)
  const [loading, setLoading] = useState(false)

  const selectableClubs = useMemo(
    () =>
      meetClubSeasons
        .map((clubSeason) => ({
          clubId: clubSeason.club.clubId || -1,
          clubName: clubSeason.club.nameLong || clubSeason.club.name || "Club",
        }))
        .filter((club, index, clubs) => {
          if (club.clubId <= 0) return false
          return clubs.findIndex((candidate) => candidate.clubId === club.clubId) === index
        }),
    [meetClubSeasons],
  )

  const getPossiblePools = useCallback(() => {
    if (selectableClubs.length === 0) {
      setAllPools([])
      return Promise.resolve()
    }

    return Promise.all(
      selectableClubs.map((club) =>
        swimminglyApi.get(gen(`/api/getClubPools/${club.clubId}`)).then((data) => {
          if (data.status !== "success" || !data.pools) {
            throw new Error("Something went wrong finding pools for meet")
          }

          return (data.pools as IPool[]).map((pool) => ({
            ...pool,
            clubId: club.clubId,
            clubName: club.clubName,
          }))
        }),
      ),
    )
      .then((poolGroups) => {
        setAllPools(poolGroups.flat())
      })
      .catch(() => {
        message.error("Something went wrong finding pools for meet")
        setAllPools([])
      })
  }, [selectableClubs])

  const getCurrentMeetPool = useCallback(() => {
    swimminglyApi
      .post(gen("/api/getCurrentMeetPool"))
      .body({ meetId })
      .then((data) => {
        if (data.status === "success" && data.meetPool) return data.meetPool
        else return null
      })
      .then((currentMeetPool) => {
        if (!currentMeetPool) {
          message.error("Problem finding current pool for this meet.")
          return
        }
        setCurrentPool(currentMeetPool)
        return
      })
  }, [meetId, setCurrentPool])

  useEffect(() => {
    if (!visible) {
      return
    }

    if (newPool?.poolId) {
      const matchingNewPool = (allPools || []).find(
        (pool) => pool.poolId === newPool.poolId,
      )
      if (matchingNewPool && matchingNewPool.clubId) {
        setSelectedClubId(matchingNewPool.clubId)
        return
      }
    }

    if (currentPool?.poolId) {
      const matchingCurrentPool = (allPools || []).find(
        (pool) => pool.poolId === currentPool.poolId,
      )
      if (matchingCurrentPool && matchingCurrentPool.clubId) {
        setSelectedClubId(matchingCurrentPool.clubId)
        return
      }
    }

    setSelectedClubId(selectableClubs[0]?.clubId || null)
  }, [allPools, currentPool, newPool?.poolId, selectableClubs, visible])

  useEffect(() => {
    if (mounted === false && visible === true && meetId) {
      Promise.all([getPossiblePools(), getCurrentMeetPool()]).then(() => {
        setMounted(true)
        return
      })
      return
    }
  }, [
    getCurrentMeetPool,
    getPossiblePools,
    setMounted,
    meetId,
    mounted,
    visible,
  ])

  const updatePool = async () => {
    if (
      !newPool ||
      !newPool.poolId ||
      !currentPool ||
      newPool.poolId === currentPool.poolId
    ) {
      message.error("Must choose a new pool for the meet.")
      return false
    }
    setLoading(true)
    return swimminglyApi
      .post(gen("/api/updateMeetPool"))
      .body({
        meetId: meetId,
        newPoolId: newPool.poolId,
        ...getActingUserRequestFields(actingUser),
      })
      .then((data) => {
        if (data.status === "success") {
          message.success("Updated meet pool successfully!")
          return true
        } else {
          message.error(data.error || "Failed to update meet pool.")
          return false
        }
      })
      .catch(() => {
        message.error("Failed to update meet pool.")
        return false
      })
      .finally(() => setLoading(false))
  }

  const closeModal = () => {
    setMounted(false)
    setVisible(false)
    setCurrentPool(null)
    setNewPool(null)
    setAllPools(null)
    setSelectedClubId(null)
  }

  const onOk = async () => {
    const didUpdate = await updatePool()
    if (!didUpdate) {
      return
    }

    closeModal()
    refresh?.()
    if (clubSeason) {
      updateSeasonMeetArrayListener.emit(
        `updateSeasonMeets${clubSeason.clubSeasonId}`,
      )
    }
  }

  const onCancel = () => {
    closeModal()
  }

  const isOkDisabled =
    !newPool ||
    !newPool.poolId ||
    !currentPool ||
    newPool.poolId === currentPool.poolId

  const currentCourseLabel = currentPool?.course_label || "Unknown"
  const nextCourseLabel = newPool?.course_label || currentCourseLabel

  const selectablePools = useMemo<IPoolSelectionOption[]>(
    () =>
      (allPools || []).map((pool) => ({
        poolId: pool.poolId || -1,
        poolName: pool.pool_name || "Pool",
        address: pool.address,
        length: pool.pool_length,
        course: pool.course_label,
        clubId: pool.clubId || -1,
        clubName: pool.clubName || null,
        description: pool.description || null,
        lat: pool.lat ?? null,
        lng: pool.lng ?? null,
        unitOfMeasure: pool.unit_of_measure ?? null,
        numLanes: pool.numLanes ?? null,
        primaryPool: !!pool.primaryPool,
      })),
    [allPools],
  )

  const supplementalContent = (
    <>
      <Alert
        type="info"
        showIcon
        title="Current meet course"
        description={`This meet is currently set to ${currentCourseLabel}.`}
        style={{ marginBottom: newPool ? 12 : 0 }}
      />
      {newPool && currentPool && currentPool.course_label !== newPool.course_label ? (
        <Alert
          type="warning"
          showIcon
          title={`Changing pool course from ${currentCourseLabel} to ${nextCourseLabel}`}
          description="If you confirm this pool change, all meet entries will be automatically converted to the new meet course."
        />
      ) : null}
    </>
  )

  return (
    <PoolSelectionModal
      visible={visible}
      title="Update Meet Pool"
      clubs={selectableClubs}
      pools={selectablePools}
      selectedClubId={selectedClubId}
      selectedPoolId={newPool?.poolId || currentPool?.poolId || null}
      onChangeClub={(clubId) => {
        setSelectedClubId(clubId)
      }}
      onSelectPool={(pool) => {
        const thePool = (allPools || []).find((candidate) => candidate.poolId === pool.poolId)
        if (!thePool) {
          message.error("Uh oh, problem finding the pool.")
          return
        }
        setNewPool(thePool)
        if (thePool.clubId) {
          setSelectedClubId(thePool.clubId)
        }
      }}
      onCancel={onCancel}
      onConfirmSelection={onOk}
      confirmButtonText="Update Pool"
      confirmDisabled={isOkDisabled}
      confirmLoading={loading}
      supplementalContent={mounted ? supplementalContent : <p>loading...</p>}
    />
  )
}
